import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { handleApiError } from '../utils/errorHandler';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  User,
  Building,
  RefreshCw,
  Link,
  ExternalLink,
  Eye,
} from 'lucide-react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import {
  fetchCustomers,
  fetchBusinessTypes,
  addCustomer,
  updateCustomer,
  deleteCustomer,
} from '../store/slices/bookingSlice';
import { customersAPI, crmAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { usePermissions } from '../hooks/usePermissions';
import PermissionGuard from '../components/PermissionGuard';
import { useCRM } from '../hooks/useCRM';
import { useDebounce } from '../hooks/useDebounce';

const customerSchema = yup.object().shape({
  name: yup.string().required('Customer name is required'),
  businessTypes: yup.array().min(1, 'At least one business type is required'),
});

const CustomerModal = ({ customer, businessTypes, onClose, onSave, isCRMActive }) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(customerSchema),
    defaultValues: customer || { businessTypes: [] },
  });

  const [businessTypeSearch, setBusinessTypeSearch] = useState('');

  const onSubmit = data => {
    onSave(data);
    reset();
  };

  useEffect(() => {
    const customerData = customer
      ? {
          ...customer,
          businessTypes: customer.businessTypes?.map(bt => bt._id) || [],
        }
      : { businessTypes: [] };
    reset(customerData);
  }, [customer, reset]);

  // Filter business types based on search term
  const filteredBusinessTypes = businessTypes.filter(bt =>
    bt.section.toLowerCase().includes(businessTypeSearch.toLowerCase())
  );

  return (
    <div className="overflow-y-auto fixed inset-0 z-50 w-full h-full bg-gray-600 bg-opacity-50">
      <div className="relative top-20 p-5 mx-auto w-full max-w-md bg-white rounded-md border shadow-lg">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          {customer ? 'Edit Customer' : 'Add New Customer'}
        </h3>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* CRM Name Display (if CRM is active and customer has CRM data) */}
          {isCRMActive && customer?.crmName && (
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">CRM Name</label>
              <div className="flex items-center p-3 bg-blue-50 rounded-md border border-blue-200">
                <Link className="mr-2 w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800">{customer.crmName}</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                This is the name from your CRM system. You can edit the customer name below.
              </p>
            </div>
          )}

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Customer Name</label>
            <input
              type="text"
              {...register('name')}
              className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. JP Plumbing"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Business Types</label>
            <Controller
              name="businessTypes"
              control={control}
              render={({ field }) => (
                <div>
                  {/* Search Input */}
                  <div className="relative mb-2">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Search className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={businessTypeSearch}
                      onChange={e => setBusinessTypeSearch(e.target.value)}
                      placeholder="Search business types..."
                      className="block py-2 pr-3 pl-9 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Business Types List */}
                  <div className="overflow-y-auto max-h-60 rounded-md border border-gray-300">
                    {filteredBusinessTypes.length > 0 ? (
                      filteredBusinessTypes.map(businessType => (
                        <label
                          key={businessType._id}
                          className="flex items-center p-2 cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            value={businessType._id}
                            checked={field.value?.includes(businessType._id) || false}
                            onChange={e => {
                              const currentValue = field.value || [];
                              if (e.target.checked) {
                                field.onChange([...currentValue, businessType._id]);
                              } else {
                                field.onChange(currentValue.filter(id => id !== businessType._id));
                              }
                            }}
                            className="mr-2 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{businessType.section}</span>
                        </label>
                      ))
                    ) : (
                      <div className="p-3 text-sm text-center text-gray-500">
                        No business types found
                      </div>
                    )}
                  </div>
                </div>
              )}
            />
            {errors.businessTypes && (
              <p className="mt-1 text-sm text-red-600">{errors.businessTypes.message}</p>
            )}
          </div>

          <div className="flex justify-end pt-4 space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {customer ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Customers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { customers, businessTypes, loading, customersPagination } = useSelector(
    state => state.booking
  );
  const { canCreate, canEdit, canDelete } = usePermissions();
  const { isCRMActive, getCRMProvider } = useCRM();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    fetchCustomersData();
    dispatch(fetchBusinessTypes());
  }, [dispatch, currentPage, itemsPerPage, debouncedSearchTerm]);

  // Handle edit customer from navigation state (from CustomerDetail page)
  useEffect(() => {
    if (location.state?.editCustomerId && customers.length > 0) {
      const customerToEdit = customers.find(c => c._id === location.state.editCustomerId);
      if (customerToEdit) {
        openModal(customerToEdit);
        // Clear the state to prevent reopening on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, customers]);

  const fetchCustomersData = () => {
    const params = {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm,
    };
    dispatch(fetchCustomers(params));
  };

  const handleSave = async data => {
    try {
      if (editingCustomer) {
        const response = await customersAPI.update(editingCustomer._id, data);
        dispatch(updateCustomer(response.data));
        toast.success('Customer updated successfully');
      } else {
        const response = await customersAPI.create(data);
        dispatch(addCustomer(response.data));
        toast.success('Customer created successfully');
      }
      setIsModalOpen(false);
      setEditingCustomer(null);
      fetchCustomersData(); // Refresh the list
    } catch (error) {
      handleApiError(error, 'Failed to save customer');
    }
  };

  const handleDelete = async customerId => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await customersAPI.delete(customerId);
        dispatch(deleteCustomer(customerId));
        toast.success('Customer deleted successfully');
        fetchCustomersData(); // Refresh the list
      } catch (error) {
        handleApiError(error, 'Failed to delete customer');
      }
    }
  };

  const openModal = (customer = null) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleSyncFromCRM = async () => {
    try {
      setSyncing(true);
      const response = await crmAPI.syncCustomers();
      toast.success(response.data.message);
      fetchCustomersData(); // Refresh the customer list
    } catch (error) {
      handleApiError(error, 'Failed to sync customers from CRM');
    } finally {
      setSyncing(false);
    }
  };

  // CRM Search functionality
  const [showCRMSearch, setShowCRMSearch] = useState(false);
  const [crmSearchTerm, setCrmSearchTerm] = useState('');
  const [crmSearchResults, setCrmSearchResults] = useState([]);
  const [crmSearching, setCrmSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const debouncedCrmSearchTerm = useDebounce(crmSearchTerm, 500);

  const searchCRMCustomers = async searchTerm => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setCrmSearchResults([]);
      return;
    }

    try {
      setCrmSearching(true);
      const response = await crmAPI.searchCustomers(searchTerm.trim());
      setCrmSearchResults(response.data.customers || []);

      // Show appropriate message based on results
      if (response.data.message) {
        toast.info(response.data.message);
      } else if (response.data.alreadyImported > 0) {
        toast.info(
          `Found ${response.data.totalFound} customers, ${response.data.alreadyImported} already imported`
        );
      } else if (response.data.customers && response.data.customers.length > 0) {
        toast.success(`Found ${response.data.customers.length} customers available to import`);
      }
    } catch (error) {
      handleApiError(error, 'Failed to search CRM customers');
      setCrmSearchResults([]);
    } finally {
      setCrmSearching(false);
    }
  };

  const handleImportCustomer = async crmCustomer => {
    try {
      setImporting(true);
      const response = await crmAPI.importCustomer({
        crmId: crmCustomer.id,
        name: crmCustomer.name,
        email: crmCustomer.email,
      });

      toast.success('Customer imported successfully');
      fetchCustomersData(); // Refresh the customer list

      // Remove imported customer from search results
      setCrmSearchResults(prev => prev.filter(c => c.id !== crmCustomer.id));
    } catch (error) {
      handleApiError(error, 'Failed to import customer');
    } finally {
      setImporting(false);
    }
  };

  // Search CRM customers when debounced search term changes
  useEffect(() => {
    if (showCRMSearch && debouncedCrmSearchTerm) {
      searchCRMCustomers(debouncedCrmSearchTerm);
    }
  }, [debouncedCrmSearchTerm, showCRMSearch]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleDownloadIndex = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/advertiser-index/csv`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download index');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'advertiser-index.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Advertiser index downloaded successfully');
    } catch (error) {
      handleApiError(error, 'Failed to download advertiser index');
    }
  };

  return (
    <Layout>
      <PermissionGuard permission="create">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
                <p className="mt-2 text-gray-600">Manage your customer database</p>
              </div>
              {/* CRM Status Indicator */}
              {isCRMActive() && (
                <div className="flex items-center px-3 py-2 text-green-800 bg-green-100 rounded-lg">
                  <Link className="mr-2 w-4 h-4" />
                  <span className="text-sm font-medium">
                    {getCRMProvider() === 'hubspot' ? 'HubSpot' : 'Capsule CRM'} Connected
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions Bar */}
          <div className="flex flex-col mb-6 space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="block py-2 pr-3 pl-10 w-full leading-5 placeholder-gray-500 bg-white rounded-md border border-gray-300 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* CRM Search Button */}
              {isCRMActive() && canCreate() && (
                <button
                  onClick={() => setShowCRMSearch(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Search className="mr-2 w-4 h-4" />
                  Search {getCRMProvider()}
                </button>
              )}

              {canCreate() && (
                <button
                  onClick={() => openModal()}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="mr-2 w-4 h-4" />
                  Add Customer
                </button>
              )}
            </div>
          </div>

          {/* Customers List */}
          {loading.customers ? (
            <div className="py-8 text-center">
              <div className="inline-block w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
              <p className="mt-2 text-gray-600">Loading customers...</p>
            </div>
          ) : customers.length > 0 ? (
            <>
              <div className="overflow-hidden bg-white shadow sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                  {customers.map(customer => (
                    <li key={customer._id}>
                      <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                        <div className="flex justify-between items-center">
                          <div className="flex flex-1 items-center min-w-0">
                            <div className="flex-shrink-0">
                              <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-full">
                                <User className="w-5 h-5 text-blue-600" />
                              </div>
                            </div>
                            <div className="flex-1 ml-4 min-w-0">
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {customer.name}
                                </p>
                                {isCRMActive() && customer.crmId && (
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                                    <Link className="mr-1 w-3 h-3" />
                                    CRM
                                  </span>
                                )}
                              </div>
                              {/* CRM Name Display */}
                              {isCRMActive() &&
                                customer.crmName &&
                                customer.crmName !== customer.name && (
                                  <div className="flex items-center mt-1">
                                    <ExternalLink className="mr-1 w-3 h-3 text-gray-400" />
                                    <span className="text-xs text-gray-500">
                                      CRM: {customer.crmName}
                                    </span>
                                  </div>
                                )}
                              <div className="flex items-center mt-1 space-x-4 text-sm text-gray-500">
                                <div className="flex flex-wrap items-center">
                                  <Building className="mr-1 w-4 h-4" />
                                  {customer.businessTypes && customer.businessTypes.length > 0 ? (
                                    customer.businessTypes.map((bt, index) => (
                                      <span
                                        key={bt._id}
                                        className="inline-flex items-center px-2 py-1 mr-1 mb-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full"
                                      >
                                        {bt.section}
                                      </span>
                                    ))
                                  ) : (
                                    <span>No business types</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => navigate(`/customers/${customer._id}`)}
                              className="p-2 text-gray-400 rounded-md hover:text-green-600 focus:outline-none focus:ring-2 focus:ring-green-500"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {canEdit() && (
                              <button
                                onClick={() => openModal(customer)}
                                className="p-2 text-gray-400 rounded-md hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                title="Edit Customer"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            {canDelete() && (
                              <button
                                onClick={() => handleDelete(customer._id)}
                                className="p-2 text-gray-400 rounded-md hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                                title="Delete Customer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={customersPagination.currentPage}
                totalPages={customersPagination.totalPages}
                totalItems={customersPagination.totalCustomers}
                itemsPerPage={customersPagination.limit}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </>
          ) : (
            <div className="py-12 text-center">
              <User className="mx-auto w-12 h-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No customers found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm
                  ? 'Try adjusting your search terms.'
                  : 'Get started by creating your first customer.'}
              </p>
              {!searchTerm && canCreate() && (
                <div className="mt-6">
                  <button
                    onClick={() => openModal()}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Plus className="mr-2 w-4 h-4" />
                    Add Customer
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Modal */}
          {isModalOpen && (
            <CustomerModal
              customer={editingCustomer}
              businessTypes={businessTypes}
              isCRMActive={isCRMActive()}
              onClose={() => {
                setIsModalOpen(false);
                setEditingCustomer(null);
              }}
              onSave={handleSave}
            />
          )}

          {/* CRM Search Modal */}
          {showCRMSearch && (
            <div className="overflow-y-auto fixed inset-0 z-50 w-full h-full bg-gray-600 bg-opacity-50">
              <div className="relative top-20 p-5 mx-auto w-full max-w-2xl bg-white rounded-md border shadow-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Search {getCRMProvider()} Customers
                  </h3>
                  <button
                    onClick={() => {
                      setShowCRMSearch(false);
                      setCrmSearchTerm('');
                      setCrmSearchResults([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>

                {/* Search Input */}
                <div className="mb-4">
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Search className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={crmSearchTerm}
                      onChange={e => setCrmSearchTerm(e.target.value)}
                      placeholder="Type customer name to search..."
                      className="block py-2 pr-3 pl-10 w-full leading-5 placeholder-gray-500 bg-white rounded-md border border-gray-300 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      autoFocus
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter at least 2 characters to search
                  </p>
                </div>

                {/* Search Results */}
                <div className="overflow-y-auto max-h-96">
                  {crmSearching && (
                    <div className="py-8 text-center">
                      <RefreshCw className="mx-auto w-8 h-8 text-blue-600 animate-spin" />
                      <p className="mt-2 text-gray-600">Searching...</p>
                    </div>
                  )}

                  {!crmSearching && crmSearchTerm.length >= 2 && crmSearchResults.length === 0 && (
                    <div className="py-8 text-center">
                      <User className="mx-auto w-12 h-12 text-gray-400" />
                      <p className="mt-2 text-gray-600">No customers found</p>
                      <p className="text-sm text-gray-500">Try a different search term</p>
                    </div>
                  )}

                  {crmSearchResults.length > 0 && (
                    <div className="space-y-2">
                      <p className="mb-3 text-sm text-gray-600">
                        Found {crmSearchResults.length} customers available to import:
                      </p>
                      {crmSearchResults.map(customer => (
                        <div
                          key={customer.id}
                          className="flex justify-between items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
                        >
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">{customer.name}</h4>
                            {customer.email && (
                              <p className="text-xs text-gray-500">{customer.email}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleImportCustomer(customer)}
                            disabled={importing}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md border border-transparent hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            {importing ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Import'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {crmSearchTerm.length < 2 && (
                    <div className="py-8 text-center">
                      <Search className="mx-auto w-12 h-12 text-gray-400" />
                      <p className="mt-2 text-gray-600">Start typing to search customers</p>
                      <p className="text-sm text-gray-500">Search by name or company</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="pt-4 mt-6 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-500">
                      Only customers not already imported are shown
                    </p>
                    <button
                      onClick={() => {
                        setShowCRMSearch(false);
                        setCrmSearchTerm('');
                        setCrmSearchResults([]);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </PermissionGuard>
    </Layout>
  );
};

export default Customers;
